import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  Copy,
  ExternalLink,
  FlaskConical,
  KeyRound,
  Loader2,
  Lock,
  Plug,
  Plus,
  Search,
  ShieldCheck,
  Webhook,
} from "lucide-react";
import { useHive, type OAuthAppInfo, type OAuthConnectOutcome } from "@/store/useHive";
import { APPS } from "@/data/apps";
import { INT_CATEGORIES, AUTH_KIND_LABEL } from "@/data/integrationsHub";
import { Modal } from "@/components/ui/Modal";
import type { IntegrationCatalogEntry } from "@/types";

const accent = APPS.integrations.accent;

function CatalogCard({ c, onPick }: { c: IntegrationCatalogEntry; onPick: () => void }) {
  return (
    <button
      onClick={onPick}
      className="flex items-start gap-2.5 rounded-xl border border-app bg-surface/50 p-3 text-left transition hover:border-app-strong"
    >
      <span
        className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-xs font-bold"
        style={{ background: `${c.color}22`, color: c.color }}
      >
        {c.glyph}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5">
          <span className="truncate text-sm font-semibold text-app">{c.name}</span>
          {c.adminOnly && <Lock size={11} className="shrink-0 text-violet-500" />}
          {c.installed && <Check size={12} className="shrink-0 text-emerald-500" />}
        </span>
        <span className="mt-0.5 line-clamp-2 block text-[11px] leading-snug text-app-2">{c.description}</span>
        <span className="mt-1.5 flex items-center gap-1.5">
          <span className="rounded bg-surface-2 px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-app-2">
            {c.category}
          </span>
          {c.testable && (
            <span className="flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide" style={{ background: "#10b9811a", color: "#10b981" }}>
              <FlaskConical size={9} /> Live test
            </span>
          )}
        </span>
      </span>
    </button>
  );
}

function OrTokenDivider() {
  return (
    <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-app-2">
      <span className="h-px flex-1" style={{ background: "hsl(var(--border))" }} />
      or paste a token
      <span className="h-px flex-1" style={{ background: "hsl(var(--border))" }} />
    </div>
  );
}

/** Copyable one-liner (URLs, scopes) used across the OAuth panels. */
function CopyField({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex items-center gap-2">
      <code className="min-w-0 flex-1 truncate rounded-lg border border-app bg-surface px-2.5 py-2 text-[11px] text-app-1">{value}</code>
      <button
        onClick={() => {
          void navigator.clipboard.writeText(value).then(() => setCopied(true));
        }}
        className="flex shrink-0 items-center gap-1 rounded-lg border border-app px-2.5 py-2 text-[11px] font-medium text-app-1 transition hover:border-app-strong"
      >
        {copied ? <Check size={12} className="text-emerald-500" /> : <Copy size={12} />} {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}

/** Success panel after a real OAuth connect. */
function OAuthConnected({ entry, outcome, onDone }: { entry: IntegrationCatalogEntry; outcome: OAuthConnectOutcome; onDone: () => void }) {
  const webhooks = outcome.providerWebhooks;
  const url = outcome.inboundHook?.url ?? null;
  const manual = webhooks?.mode !== "auto";
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
      <div className="flex items-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3">
        <ShieldCheck size={16} className="shrink-0 text-emerald-500" />
        <div>
          <div className="text-sm font-semibold text-app">{entry.name} connected via OAuth</div>
          <p className="text-[11px] leading-relaxed text-app-2">
            Tokens live in your self-hosted Nango — Hive only ever calls {entry.name} through its authenticated proxy.
          </p>
        </div>
      </div>

      {webhooks?.mode === "auto" && (
        <div className="flex items-start gap-2 rounded-xl border border-app bg-surface-2/30 p-3">
          <Webhook size={14} className="mt-0.5 shrink-0 text-emerald-500" />
          <p className="text-[11px] leading-relaxed text-app-1">
            <span className="font-semibold text-app">Events wired automatically.</span> {webhooks.detail}. Matching
            workflows will trigger with no further setup.
          </p>
        </div>
      )}

      {manual && url && (
        <div className="rounded-xl border border-app bg-surface-2/30 p-3">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-app">
            <Webhook size={13} /> One step left in {entry.name}: register the event endpoint
          </div>
          {webhooks?.detail && <p className="mt-1 text-[11px] leading-relaxed text-amber-500">{webhooks.detail}</p>}
          <p className="mt-1 text-[11px] leading-relaxed text-app-2">
            Paste this URL in {entry.name}'s webhook settings (Jira: <span className="font-medium">Settings → System → WebHooks</span>,
            events "Issue created" and "Issue updated") so {entry.name} events can trigger your workflows.
          </p>
          <div className="mt-2">
            <CopyField value={url} />
          </div>
          <p className="mt-1.5 text-[10px] text-app-2">Find it again later under Hooks → inbound → "{entry.name} events".</p>
        </div>
      )}

      <button
        onClick={onDone}
        className="w-full rounded-lg py-2.5 text-sm font-semibold text-black transition hover:brightness-105"
        style={{ background: accent }}
      >
        Done
      </button>
    </motion.div>
  );
}

/**
 * Admin one-time setup: register the provider's OAuth app entirely from the
 * UI (create the app in the provider's console, paste id + secret here).
 */
function OAuthAppSetup({ entry }: { entry: IntegrationCatalogEntry }) {
  const fetchOAuthApp = useHive((s) => s.fetchOAuthApp);
  const setupOAuthApp = useHive((s) => s.setupOAuthApp);
  const [info, setInfo] = useState<OAuthAppInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [scopes, setScopes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void fetchOAuthApp(entry.id).then((res) => {
      if (!alive) return;
      setInfo(res);
      setScopes(res?.scopes ?? res?.recommendedScopes ?? "");
      setClientId(res?.clientId ?? "");
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [entry.id, fetchOAuthApp]);

  const save = async () => {
    setError(null);
    setSaving(true);
    const outcome = await setupOAuthApp(entry.id, clientId.trim(), clientSecret.trim(), scopes);
    setSaving(false);
    if (!outcome.ok) setError(outcome.error ?? "Could not save — retry");
    // On success the catalogue reloads and the parent re-renders into the
    // ready state (Connect with …) automatically.
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 rounded-xl border border-app bg-surface-2/30 p-4 text-xs text-app-2">
        <Loader2 size={13} className="animate-spin" /> Checking OAuth setup…
      </div>
    );
  }
  if (!info?.enabled) return null;

  return (
    <div className="space-y-2.5 rounded-xl border border-app bg-surface-2/30 p-3">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-app">
        <KeyRound size={13} /> One-time setup: {entry.name} OAuth app
      </div>
      <p className="text-[11px] leading-relaxed text-app-2">
        Create an OAuth app in{" "}
        <a href={info.appUrl ?? entry.docsUrl} target="_blank" rel="noreferrer" className="underline decoration-dotted" style={{ color: accent }}>
          {entry.name}'s developer console <ExternalLink size={9} className="inline" />
        </a>{" "}
        with the callback URL below, then paste its credentials here. After this, everyone in the org connects with one click.
      </p>
      {info.callbackUrl && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-app-2">Callback / redirect URL</div>
          <div className="mt-1">
            <CopyField value={info.callbackUrl} />
          </div>
        </div>
      )}
      <div>
        <label className="text-[10px] uppercase tracking-wider text-app-2">Client ID</label>
        <input
          value={clientId}
          onChange={(e) => setClientId(e.target.value)}
          placeholder="From the provider's app page"
          className="mt-1 w-full rounded-lg border border-app bg-surface px-3 py-2 text-sm text-app outline-none transition focus:border-app-strong placeholder:text-app-2"
        />
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-wider text-app-2">Client secret</label>
        <input
          type="password"
          value={clientSecret}
          onChange={(e) => setClientSecret(e.target.value)}
          placeholder={info.configured ? "Stored — paste again to rotate" : "Shown once when you create the app"}
          className="mt-1 w-full rounded-lg border border-app bg-surface px-3 py-2 text-sm text-app outline-none transition focus:border-app-strong placeholder:text-app-2"
        />
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-wider text-app-2">Scopes (prefilled with recommended)</label>
        <input
          value={scopes}
          onChange={(e) => setScopes(e.target.value)}
          placeholder="Provider defaults when empty"
          className="mt-1 w-full rounded-lg border border-app bg-surface px-3 py-2 text-xs text-app outline-none transition focus:border-app-strong placeholder:text-app-2"
        />
      </div>
      {error && (
        <p className="flex items-center gap-1 text-[11px] text-rose-500">
          <AlertTriangle size={11} /> {error}
        </p>
      )}
      <button
        disabled={saving || !clientId.trim() || !clientSecret.trim()}
        onClick={() => void save()}
        className="flex w-full items-center justify-center gap-1.5 rounded-lg py-2.5 text-sm font-semibold text-black transition hover:brightness-105 disabled:opacity-50"
        style={{ background: accent }}
      >
        {saving ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
        {info.configured ? "Update OAuth app" : "Save & enable OAuth"}
      </button>
    </div>
  );
}

function ConnectForm({ entry, onBack, onDone }: { entry: IntegrationCatalogEntry; onBack: () => void; onDone: () => void }) {
  const installIntegration = useHive((s) => s.installIntegration);
  const connectIntegration = useHive((s) => s.connectIntegration);
  const connectOAuth = useHive((s) => s.connectOAuth);
  // The catalogue reloads when an admin saves an OAuth app — read the live
  // entry so this form flips to "Connect with …" without reopening.
  const live = useHive((s) => s.catalog.find((c) => c.id === entry.id)) ?? entry;
  const meRole = useHive((s) => s.meRole);
  const oauthInfra = useHive((s) => s.catalogOauth.enabled);
  // Live install state: after a rejected first attempt the connector IS
  // installed, so the retry must go through connect, not install.
  const installed = live.installed;
  const [secret, setSecret] = useState("");
  const [baseUrl, setBaseUrl] = useState("");
  const [busy, setBusy] = useState<"connect" | "install" | "oauth" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [oauthDone, setOauthDone] = useState<OAuthConnectOutcome | null>(null);

  const needsSecret = entry.authKind !== "none" && entry.authKind !== "webhook";
  const secretLabel = entry.authKind === "api-key" ? "API key" : "Access token";
  const oauthReady = live.oauthReady === true;
  const oauthSupported = live.oauthSupported === true;
  const isAdmin = meRole === "owner" || meRole === "admin";

  const submit = async (withCredentials: boolean) => {
    setError(null);
    setBusy(withCredentials ? "connect" : "install");
    const credentials = withCredentials
      ? {
          ...(entry.authKind === "api-key" ? { apiKey: secret } : { token: secret }),
          ...(baseUrl.trim() ? { baseUrl: baseUrl.trim() } : {}),
        }
      : undefined;
    const outcome = installed
      ? await connectIntegration(entry.id, credentials)
      : await installIntegration(entry.id, credentials);
    setBusy(null);
    if (outcome.ok) onDone();
    else setError(outcome.error ?? "Something went wrong — retry");
  };

  const startOAuth = async () => {
    setError(null);
    setBusy("oauth");
    const outcome = await connectOAuth(entry.id);
    setBusy(null);
    if (outcome.ok) setOauthDone(outcome);
    else setError(outcome.error ?? "The OAuth flow failed — retry");
  };

  if (oauthDone) return <OAuthConnected entry={entry} outcome={oauthDone} onDone={onDone} />;

  return (
    <motion.div initial={{ opacity: 0, x: 14 }} animate={{ opacity: 1, x: 0 }} className="space-y-4">
      <button onClick={onBack} className="flex items-center gap-1 text-xs text-app-2 transition hover:text-app">
        <ArrowLeft size={12} /> All connectors
      </button>

      <div className="flex items-start gap-3">
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl text-sm font-bold" style={{ background: `${entry.color}22`, color: entry.color }}>
          {entry.glyph}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 text-sm font-semibold text-app">
            {entry.name}
            {entry.adminOnly && <Lock size={12} className="text-violet-500" />}
          </div>
          <p className="mt-0.5 text-xs leading-relaxed text-app-2">{entry.description}</p>
          <a
            href={entry.docsUrl}
            target="_blank"
            rel="noreferrer"
            className="mt-1 inline-flex items-center gap-1 text-[11px] transition hover:brightness-110"
            style={{ color: accent }}
          >
            API documentation <ExternalLink size={10} />
          </a>
        </div>
      </div>

      {/* scopes preview */}
      <div className="rounded-xl border border-app bg-surface-2/30 p-3">
        <div className="text-[10px] uppercase tracking-wide text-app-2">Hive will be able to read</div>
        <div className="mt-1 flex flex-wrap gap-1">
          {entry.read.map((r) => (
            <span key={r} className="rounded bg-surface-2 px-1.5 py-0.5 text-[10px] text-app-1">{r}</span>
          ))}
        </div>
        <div className="mt-2 text-[10px] uppercase tracking-wide text-app-2">And write</div>
        <div className="mt-1 flex flex-wrap gap-1">
          {entry.write.map((w) => (
            <span key={w} className="rounded px-1.5 py-0.5 text-[10px]" style={{ background: `${accent}1a`, color: accent }}>{w}</span>
          ))}
        </div>
      </div>

      {/* real OAuth (self-hosted Nango) — the preferred path when configured */}
      {oauthReady && (
        <div className="space-y-2">
          <button
            disabled={busy !== null}
            onClick={() => void startOAuth()}
            className="flex w-full items-center justify-center gap-1.5 rounded-lg py-2.5 text-sm font-semibold text-white transition hover:brightness-110 disabled:opacity-60"
            style={{ background: entry.color }}
          >
            {busy === "oauth" ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
            {busy === "oauth" ? "Waiting for authorization…" : `Connect with ${entry.name}`}
          </button>
          <p className="text-center text-[11px] leading-relaxed text-app-2">
            {busy === "oauth"
              ? "Finish authorizing in the popup — this closes by itself."
              : "Authorize in a popup. Tokens are held by your self-hosted Nango, never by Hive."}
          </p>
          <OrTokenDivider />
        </div>
      )}

      {/* OAuth possible but the provider app isn't registered yet */}
      {!oauthReady && oauthSupported && oauthInfra && isAdmin && (
        <div className="space-y-2">
          <OAuthAppSetup entry={live} />
          <OrTokenDivider />
        </div>
      )}
      {!oauthReady && oauthSupported && oauthInfra && !isAdmin && (
        <div className="space-y-2">
          <p className="rounded-xl border border-dashed border-app p-3 text-[11px] leading-relaxed text-app-2">
            <ShieldCheck size={11} className="mr-1 inline text-app-2" />
            {entry.name} supports one-click OAuth, but an <span className="font-medium text-app-1">org admin</span> must
            register the {entry.name} OAuth app first (a one-time step done right here in this dialog). Until then you
            can connect with a personal access token below.
          </p>
          <OrTokenDivider />
        </div>
      )}
      {!oauthReady && oauthSupported && !oauthInfra && isAdmin && (
        <div className="space-y-2">
          <p className="rounded-xl border border-dashed border-app p-3 text-[11px] leading-relaxed text-app-2">
            {entry.name} supports one-click OAuth, but this deployment has no OAuth service configured
            (the self-hosted Nango stack isn't running or Hive isn't pointed at it). Token connect still works below.
          </p>
          <OrTokenDivider />
        </div>
      )}

      {/* credential form */}
      {needsSecret ? (
        <div className="space-y-2.5">
          <div>
            <label className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-app-2">
              <KeyRound size={11} /> {secretLabel}
              <span className="normal-case text-app-2/70">· {AUTH_KIND_LABEL[entry.authKind]}</span>
            </label>
            <input
              type="password"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              placeholder={entry.authKind === "api-key" ? "sk-…" : "Paste a personal access token"}
              className="mt-1.5 w-full rounded-lg border border-app bg-surface px-3 py-2 text-sm text-app outline-none transition focus:border-app-strong placeholder:text-app-2"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-app-2">Base URL (self-hosted, optional)</label>
            <input
              type="url"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              placeholder="https://…"
              className="mt-1.5 w-full rounded-lg border border-app bg-surface px-3 py-2 text-sm text-app outline-none transition focus:border-app-strong placeholder:text-app-2"
            />
          </div>
          {entry.authKind === "oauth" && !oauthReady && !oauthSupported && (
            <p className="text-[11px] leading-relaxed text-app-2">
              {entry.name} uses OAuth in production. Until an OAuth app is registered for your org, paste a
              personal access token — it is stored server-side and never shown again.
            </p>
          )}
          {entry.testable && (
            <p className="flex items-center gap-1 text-[11px] text-emerald-500">
              <FlaskConical size={11} /> The credential is verified live against the {entry.name} API.
            </p>
          )}
        </div>
      ) : (
        <p className="rounded-lg border border-dashed border-app p-3 text-xs leading-relaxed text-app-2">
          {entry.authKind === "webhook"
            ? `${entry.name} connects over webhooks — install it and Hive will start accepting inbound events for your org.`
            : `${entry.name} needs no credentials — it is wired through your organization's platform config.`}
        </p>
      )}

      {/* live failure — stays visible so the credential can be fixed in place */}
      {error && (
        <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 p-3">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-rose-500">
            <AlertTriangle size={13} /> Connection failed
          </div>
          <p className="mt-1 text-[11px] leading-relaxed text-app-1">{error}</p>
          <p className="mt-1 text-[11px] leading-relaxed text-app-2">
            Check the credential and try again — the{" "}
            <a href={entry.docsUrl} target="_blank" rel="noreferrer" className="underline decoration-dotted" style={{ color: accent }}>
              {entry.name} API docs
            </a>{" "}
            list the required scopes.
          </p>
        </div>
      )}

      <div className="flex items-center gap-2">
        <button
          disabled={busy !== null || (needsSecret && !secret.trim())}
          onClick={() => void submit(needsSecret)}
          className="flex flex-1 items-center justify-center gap-1.5 rounded-lg py-2.5 text-sm font-semibold text-black transition hover:brightness-105 disabled:opacity-50"
          style={{ background: accent }}
        >
          {busy === "connect" ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
          {error ? "Retry" : installed ? "Connect" : "Install & connect"}
        </button>
        {needsSecret && !installed && (
          <button
            disabled={busy !== null}
            onClick={() => void submit(false)}
            className="rounded-lg border border-app px-3 py-2.5 text-xs font-medium text-app-1 transition hover:border-app-strong disabled:opacity-50"
            title="Install now, add credentials later"
          >
            {busy === "install" ? <Loader2 size={13} className="animate-spin" /> : "Install only"}
          </button>
        )}
      </div>
    </motion.div>
  );
}

/** Browse the full connector catalogue and install providers into the org. */
export function ConnectAppModal() {
  const open = useHive((s) => s.connectCatalogOpen);
  const setOpen = useHive((s) => s.setConnectCatalog);
  const catalog = useHive((s) => s.catalog);
  const catalogStatus = useHive((s) => s.catalogStatus);
  const loadCatalog = useHive((s) => s.loadCatalog);

  const target = useHive((s) => s.connectCatalogTarget);

  const [q, setQ] = useState("");
  const [cat, setCat] = useState<string>("All");
  const [picked, setPicked] = useState<IntegrationCatalogEntry | null>(null);

  useEffect(() => {
    if (open && catalogStatus !== "ready") void loadCatalog();
    if (!open) {
      setPicked(null);
      setQ("");
      setCat("All");
    }
  }, [open, catalogStatus, loadCatalog]);

  // Deep link: a card's Connect button opens the catalogue on that provider.
  useEffect(() => {
    if (open && target && catalogStatus === "ready") {
      const entry = catalog.find((c) => c.id === target);
      if (entry) setPicked(entry);
    }
  }, [open, target, catalogStatus, catalog]);

  const categories = useMemo(() => {
    const present = new Set<string>(catalog.map((c) => c.category));
    return ["All", ...INT_CATEGORIES.filter((c) => present.has(c))];
  }, [catalog]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return catalog.filter(
      (c) =>
        (cat === "All" || c.category === cat) &&
        (!needle || c.name.toLowerCase().includes(needle) || c.description.toLowerCase().includes(needle))
    );
  }, [catalog, q, cat]);

  const connected = catalog.filter((c) => c.installed).length;

  return (
    <Modal
      open={open}
      onClose={() => setOpen(false)}
      accent={accent}
      icon={<Plug size={18} />}
      title="Connect an app"
      subtitle={`${catalog.length} connectors · ${connected} installed in your org`}
      width={860}
    >
      {picked ? (
        <ConnectForm entry={picked} onBack={() => setPicked(null)} onDone={() => setOpen(false)} />
      ) : (
        <div className="flex flex-col gap-3">
          {/* search */}
          <div className="flex items-center gap-2 rounded-xl border border-app bg-surface px-3 py-2">
            <Search size={14} className="shrink-0 text-app-2" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search Slack, Datadog, Stripe, Figma…"
              className="w-full bg-transparent text-sm text-app outline-none placeholder:text-app-2"
            />
            {q && (
              <button onClick={() => setQ("")} className="text-[11px] text-app-2 hover:text-app">
                clear
              </button>
            )}
          </div>

          {/* category pills */}
          <div className="flex items-center gap-1 overflow-x-auto no-scrollbar">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                className="shrink-0 rounded-lg px-2.5 py-1.5 text-xs font-medium transition"
                style={{
                  background: cat === c ? `${accent}1f` : "transparent",
                  color: cat === c ? accent : "var(--text-2)",
                }}
              >
                {c}
              </button>
            ))}
          </div>

          {/* results */}
          <div className="grid max-h-[46vh] grid-cols-1 content-start gap-2 overflow-y-auto no-scrollbar sm:grid-cols-2">
            {catalogStatus === "loading" && catalog.length === 0 ? (
              <div className="col-span-full flex items-center justify-center gap-2 py-10 text-sm text-app-2">
                <Loader2 size={15} className="animate-spin" /> Loading catalogue…
              </div>
            ) : catalogStatus === "error" && catalog.length === 0 ? (
              <div className="col-span-full py-10 text-center text-sm text-app-2">
                Couldn't load the catalogue.{" "}
                <button onClick={() => void loadCatalog()} className="font-medium" style={{ color: accent }}>
                  Retry
                </button>
              </div>
            ) : filtered.length === 0 ? (
              <div className="col-span-full py-10 text-center text-sm text-app-2">
                No connectors match "{q}"
              </div>
            ) : (
              filtered.map((c) => <CatalogCard key={c.id} c={c} onPick={() => setPicked(c)} />)
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}
